import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  REQUEST_MINIMUM_PROBED_TOPICS,
  REQUEST_PROBE_TOPICS,
  REQUEST_PROBE_TOPIC_LABELS,
  evaluateProbeCoverage,
} from '@dha-nexus/shared';

import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../components/primitives.jsx';
import { RcaReviewSection } from './RcaReviewSection.jsx';
import {
  appendRequestMessage,
  approveRequestAnalysis,
  createRequestIntake,
  generateRequestAnalysis,
  getRequestIntake,
  newIdempotencyKey,
  patchRequestIntake,
  prepareRequestIntake,
  requestNextProbe,
  submitRequestIntake,
} from './requestIntakeApi.js';
import { useIntakeDraftAutosave } from './useIntakeDraftAutosave.js';
import './feedbackIntake.css';

// Feature/service-request intake (typed help/intake framework, commit 4).
//
// A CONVERSATION, then a hypothesis the reporter confirms, then a submission.
//
// The three things this page is careful about:
//
// 1. It never claims AI involvement it cannot prove. Each assistant turn carries a
//    server-set `source`, and the label is chosen from that — "Suggested by PRIME's
//    AI assistant" only when the server says a real model authored it, and a plain
//    guide-question label otherwise. The page has no fallback that guesses.
// 2. An AI outage is a normal state, not an error screen. The probe endpoint always
//    returns a question; a failed analysis offers the deterministic template in the
//    same breath as the failure message, so the reporter is never stuck.
// 3. The reporter's typed work is never discarded. Autosave is edit-sequence guarded
//    (useIntakeDraftAutosave), a revision conflict refreshes and keeps what is on
//    screen, and the message box is only cleared once the server has the turn.

const STEPS = Object.freeze([
  { id: 'converse', label: 'Describe what you need' },
  { id: 'analysis', label: 'Check the analysis' },
  { id: 'submitted', label: 'Confirmation' },
]);

const MESSAGE_MAX_LENGTH = 4000;
const TITLE_MAX_LENGTH = 400;

// The label for an assistant turn. `source` is server-set; anything other than the
// literal 'ai' is presented as PRIME's own guide, which is what an outage, a mock,
// and a fallback all produce.
export function turnSourceLabel(message) {
  if (message?.source === 'ai') return 'Suggested by PRIME’s AI assistant';
  if (message?.kind === 'preliminary_rca' || message?.payload?.kind === 'preliminary_rca') {
    return 'From PRIME';
  }
  return 'From PRIME’s question guide';
}

function messageTopic(message) {
  return message?.topic ?? message?.payload?.topic ?? null;
}

function messageSource(message) {
  return message?.source ?? message?.payload?.source ?? null;
}

export function RequestIntakePage() {
  const { intakeId: routeIntakeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCurrentUser();

  const [step, setStep] = useState('converse');
  const [intake, setIntake] = useState(null);
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState('');
  const [rcaContent, setRcaContent] = useState(null);
  const [rcaDirty, setRcaDirty] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loadState, setLoadState] = useState(routeIntakeId ? 'loading' : 'ready');
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  // The operation is believed to be running on the server while this page is no longer
  // watching it. See generateAnalysis for why this is a separate state from `generating`.
  const [analysisPending, setAnalysisPending] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [analysisFailure, setAnalysisFailure] = useState(null);
  const [liveNote, setLiveNote] = useState('');
  const [validationKeys, setValidationKeys] = useState([]);
  const [submission, setSubmission] = useState(null);

  // One key per logical create and per logical submit, held for the life of the
  // attempt: a retry after an ambiguous failure must reuse it so the server replays
  // rather than creating a second intake or a second governed record.
  const createKeyRef = useRef(newIdempotencyKey());
  const submitKeyRef = useRef(newIdempotencyKey());
  // ONE logical analysis operation per intake at a time, identified by ONE key.
  //
  // "Stop waiting" stops WATCHING; it does not and cannot cancel the server operation
  // (aborting a browser fetch does not abort Express or Bedrock). So the key is
  // RETAINED: a later request with it either replays the finished result or reports
  // IDEMPOTENCY_IN_PROGRESS. A fresh key is minted only when the operation reaches a
  // terminal state — completed (the reporter may then regenerate under the existing
  // unapproved-replace rule) or released/failed (retry).
  //
  // The earlier version minted a new UI "attempt" on cancel while reusing this key,
  // which could not supersede anything: it either hit IDEMPOTENCY_IN_PROGRESS or
  // replayed the very result the reporter had just cancelled.
  const analysisKeyRef = useRef(newIdempotencyKey());
  const analysisModeRef = useRef('ai');
  // One key per logical "ask the next question": a retry after an ambiguous failure
  // replays that turn instead of spending a second model call, and a fresh key is
  // minted once the turn lands so the NEXT question is a new operation.
  const probeKeyRef = useRef(newIdempotencyKey());
  const intakeRef = useRef(null);
  const rcaDirtyRef = useRef(false);
  // Bumped on every analysis edit; persist() compares it across the PATCH so an
  // edit typed mid-save is never overwritten by the returning server copy.
  const rcaEditSequenceRef = useRef(0);
  // P2-2: monotonic attempt id + AbortController, so a superseded generation can never
  // write state or re-enable the button after a newer attempt has started.
  const generateAttemptRef = useRef(0);
  const generateAbortRef = useRef(null);
  const messageInputRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const validationSummaryRef = useRef(null);

  intakeRef.current = intake;

  const sourceRoute = location.state?.fromRoute || null;

  /**
   * Adopt a server copy of the intake.
   *
   * `{ keepLocalRca: true }` is used by the CONFLICT refresh path. Overwriting the
   * analysis there would hand the reporter another tab's text in place of their own
   * unsaved edit and silently clear the dirty marker, so the next save would omit
   * `preliminaryRca` entirely and their work would be gone. The server copy of
   * everything else (revision above all) is still adopted, which is what lets the
   * retry succeed.
   */
  const applyIntake = useCallback((loaded, { keepLocalRca = false } = {}) => {
    setIntake(loaded);
    intakeRef.current = loaded;
    if (Array.isArray(loaded?.messages)) setMessages(loaded.messages);
    if (keepLocalRca && rcaDirtyRef.current) return loaded;
    if (loaded?.preliminaryRca?.content) {
      setRcaContent(loaded.preliminaryRca.content);
      rcaDirtyRef.current = false;
      setRcaDirty(false);
    }
    return loaded;
  }, []);

  const buildPayload = useCallback(() => {
    const payload = {
      title: title.trim() || null,
      details: { description },
      ...(sourceRoute ? { sourceRoute } : {}),
    };
    // The analysis subtree rides along ONLY when the reporter actually edited it.
    // Resending it otherwise would clear their confirmation for nothing — an RCA
    // patch always invalidates the approval, by design.
    if (rcaDirtyRef.current && rcaContent) payload.preliminaryRca = rcaContent;
    return payload;
  }, [description, rcaContent, sourceRoute, title]);

  const persist = useCallback(
    async (payload) => {
      const current = intakeRef.current;
      if (!current) {
        const response = await createRequestIntake(payload, {
          idempotencyKey: createKeyRef.current,
        });
        const created = applyIntake(response?.data);
        // Give the draft a durable URL so a refresh or a closed tab resumes it.
        navigate(`/feedback/request/${created.id}`, {
          replace: true,
          state: location.state || null,
        });
        return created;
      }
      // Captured BEFORE the PATCH: if the reporter edits the analysis while the
      // request is in flight, adopting the server copy afterwards would overwrite
      // their newer text on screen, and clearing the dirty marker would drop it
      // from the next save's payload — silent data loss on the SUCCESS path, the
      // same defect the conflict path already guards against with keepLocalRca.
      const rcaEditsAtSend = rcaEditSequenceRef.current;
      const response = await patchRequestIntake(current.id, {
        expectedRevision: current.revision,
        ...payload,
      });
      const rcaEditedMidFlight = rcaEditSequenceRef.current !== rcaEditsAtSend;
      const saved = applyIntake(response?.data, { keepLocalRca: rcaEditedMidFlight });
      if (payload.preliminaryRca && !rcaEditedMidFlight) {
        rcaDirtyRef.current = false;
        setRcaDirty(false);
      }
      return saved;
    },
    [applyIntake, location.state, navigate],
  );

  const onConflict = useCallback(async () => {
    try {
      const refreshed = await getRequestIntake(intakeRef.current.id);
      // keepLocalRca: the reporter's unsaved analysis text and its dirty marker
      // survive the refresh. Only the revision (and the rest of the aggregate) is
      // adopted, so the next save wins instead of losing their edit.
      applyIntake(refreshed?.data, { keepLocalRca: true });
      return 'This request was changed in another tab. Your text is still here — save again to keep it.';
    } catch {
      return 'This request was changed elsewhere and PRIME could not reload it.';
    }
  }, [applyIntake]);

  const { saveState, saveMessage, setSaveMessage, markDirty, save, isDirty } =
    useIntakeDraftAutosave({
      buildPayload,
      persist,
      enabled: step !== 'submitted',
      watch: [title, description, rcaContent],
      onConflict,
      describeError: (error) =>
        error?.message
          ? `PRIME could not save your draft: ${error.message}`
          : 'PRIME could not save your draft. Your text is still here — try again.',
    });

  // Resume a draft. Guarded on `intakeRef.current` because the FIRST save replaces
  // the URL with the draft's own route, which changes `routeIntakeId` from undefined
  // to the new id; without the guard that navigation would re-fetch and overwrite
  // whatever the reporter typed during the save round trip.
  useEffect(() => {
    if (!routeIntakeId || intakeRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getRequestIntake(routeIntakeId);
        if (cancelled) return;
        const loaded = applyIntake(response?.data);
        setTitle(loaded?.title || '');
        setDescription(loaded?.details?.description || '');
        if (loaded?.status === 'submitted') {
          setStep('submitted');
          setSubmission({ intake: loaded });
        } else if (loaded?.preliminaryRca) {
          setStep('analysis');
        }
        setLoadState('ready');
      } catch (error) {
        if (cancelled) return;
        setLoadError(error);
        setLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyIntake, routeIntakeId]);

  // Keep the newest turn in view as the conversation grows. Optional-chained
  // because jsdom does not implement it and this is presentation only.
  useEffect(() => {
    if (!messages.length) return;
    transcriptEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length]);

  const coverage = useMemo(() => evaluateProbeCoverage({ messages }), [messages]);
  const assistantTurns = messages.filter((message) => message.actorType === 'assistant');
  const lastAssistantTurn = assistantTurns[assistantTurns.length - 1] || null;

  // ONE announcement channel. The live region renders `saveMessage || liveNote`, so
  // announcing progress has to clear a stale autosave line first — otherwise a
  // "Draft saved" from a second ago masks "PRIME is drafting the analysis", which is
  // the more important thing a screen-reader user needs to hear right now.
  const announce = useCallback(
    (text) => {
      setSaveMessage('');
      setLiveNote(text);
    },
    [setSaveMessage],
  );

  const ensureIntake = useCallback(async () => {
    if (intakeRef.current) return intakeRef.current;
    return persist(buildPayload());
  }, [buildPayload, persist]);

  /**
   * A SUCCESSFUL draft flush is a hard prerequisite for every governance action.
   *
   * `save()` returns null for a failure OR a revision conflict. The previous code
   * wrote `(await save()) || intakeRef.current`, which turned a failed persist into a
   * usable "saved" result and let approve/prepare/submit proceed against the server's
   * older copy — the reporter would confirm and send text the server never received,
   * and no amount of server-side revalidation can detect an edit that never arrived.
   *
   * So the failure stops the action, says so plainly, and names which action stopped.
   */
  const requireSavedDraft = useCallback(
    async (actionLabel) => {
      const saved = await save();
      if (!saved) {
        setActionError(
          `PRIME could not save your latest changes, so nothing was sent. ${actionLabel} was stopped — your text is still here. Try again.`,
        );
        return null;
      }
      // A successful PATCH is not enough: an edit typed while it was in flight means
      // the saved aggregate is already older than what is on screen. Proceeding would
      // send text the reporter can see but the server never received.
      if (isDirty()) {
        setActionError(
          `You kept typing while PRIME was saving, so nothing was sent. ${actionLabel} was stopped — your text is still here. Try again.`,
        );
        return null;
      }
      return saved;
    },
    [save, isDirty],
  );

  async function askNextQuestion() {
    setActionError(null);
    setBusy(true);
    try {
      const current = await ensureIntake();
      const response = await requestNextProbe(current.id, current.revision, {
        idempotencyKey: probeKeyRef.current,
      });
      probeKeyRef.current = newIdempotencyKey();
      applyIntake(response?.data?.intake);
      const probe = response?.data?.probe || response?.data?.message;
      setMessages((existing) => [...existing, probe]);
      announce(`PRIME asked: ${probe.content}`);
      // Focus the answer box so a keyboard user is taken straight to their turn.
      // Done synchronously, not on a timer: the textarea is always mounted on this
      // step, and a deferred focus is a focus that can be missed.
      messageInputRef.current?.focus();
    } catch (error) {
      if (error?.details?.reason === 'probe_topics_exhausted') {
        announce('PRIME has asked everything on its list. You can generate the analysis now.');
      } else {
        setActionError(
          error?.message || 'PRIME could not add the next question. Your draft is still saved.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendAnswer() {
    const content = draft.trim();
    if (!content) return;
    setActionError(null);
    setBusy(true);
    try {
      const current = await ensureIntake();
      const response = await appendRequestMessage(current.id, current.revision, content);
      // Cleared only once the server has it, so a failure never loses the answer.
      setDraft('');
      const message = response?.data?.message;
      setMessages((existing) => [...existing, message]);
      applyIntake(response?.data?.intake);
      announce('Your answer was added.');
    } catch (error) {
      setActionError(
        error?.message || 'PRIME could not add your answer. Your text is still here — try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  function handleDraftKeyDown(event) {
    // Enter sends, Shift+Enter makes a newline — the convention every reporter
    // already knows from chat tools.
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void sendAnswer();
  }

  /**
   * Start — or resume watching — the ONE analysis operation for this intake.
   *
   * `resume: true` is the "check if it is ready yet" path: same key, same mode, no new
   * logical operation. Everything else is identical, which is the point — the server's
   * idempotency claim is what decides whether this call generates, replays, or reports
   * that work is still in flight.
   */
  async function generateAnalysis(mode, { resume = false } = {}) {
    const effectiveMode = resume ? analysisModeRef.current : mode;
    analysisModeRef.current = effectiveMode;
    setActionError(null);
    setAnalysisFailure(null);
    // Attempt ids only decide whether a RESPONSE may touch state. They no longer imply
    // a second server operation.
    generateAbortRef.current?.abort();
    const attempt = generateAttemptRef.current + 1;
    generateAttemptRef.current = attempt;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    generateAbortRef.current = controller;
    const isCurrent = () => generateAttemptRef.current === attempt;
    setGenerating(true);
    try {
      // Flush pending edits FIRST, then announce. The autosave reports its own
      // "Draft saved" through the same live region, so announcing before the save
      // would have that overwrite the progress message the reporter needs. A FAILED
      // flush stops here: drafting an analysis from a conversation the server does
      // not have would analyse the wrong request.
      const saved = await requireSavedDraft(
        resume ? 'Checking the analysis' : 'Generating the analysis',
      );
      if (!saved) return;
      announce(
        resume
          ? 'PRIME is checking whether the analysis has finished…'
          : effectiveMode === 'deterministic'
            ? 'PRIME is preparing a template from your answers…'
            : 'PRIME is drafting a preliminary analysis. This can take a few seconds…',
      );
      const response = await generateRequestAnalysis(saved.id, saved.revision, {
        mode: effectiveMode,
        idempotencyKey: analysisKeyRef.current,
        signal: controller?.signal,
      });
      if (!isCurrent()) return;
      // Terminal: the operation completed (freshly or replayed). The key has served its
      // purpose, so the next generation is a new operation.
      analysisKeyRef.current = newIdempotencyKey();
      setAnalysisPending(false);
      applyIntake(response?.data?.intake);
      setAcknowledged(false);
      setStep('analysis');
      announce('The preliminary analysis is ready for you to check.');
    } catch (error) {
      if (!isCurrent() || error?.name === 'AbortError') return;
      if (error?.code === 'IDEMPOTENCY_IN_PROGRESS') {
        // Still running server side. NOT a second provider call, and not an error the
        // reporter caused — the honest report is that PRIME is still working.
        setAnalysisPending(true);
        announce(
          'PRIME is still working on the analysis in the background. Check again in a moment.',
        );
        return;
      }
      if (error?.code === 'PRELIMINARY_ANALYSIS_UNAVAILABLE') {
        // Terminal failure: the server released the claim, so the key is spent.
        analysisKeyRef.current = newIdempotencyKey();
        setAnalysisPending(false);
        setAnalysisFailure({
          reason: error?.details?.reason || 'model_output_invalid',
          message: error.message,
        });
      } else if (error?.details?.reason === 'analysis_already_approved') {
        analysisKeyRef.current = newIdempotencyKey();
        setAnalysisPending(false);
        setStep('analysis');
        setActionError(error.message);
      } else {
        // Ambiguous (network, 5xx): the operation MAY be running, so the key is kept and
        // the reporter is offered the resume path rather than a blind retry.
        setAnalysisPending(true);
        setActionError(
          error?.message ||
            'PRIME could not confirm whether the analysis started. Use “Check if it is ready” in a moment.',
        );
      }
    } finally {
      // Only the CURRENT attempt may clear the busy state; a superseded one must not
      // re-enable the button while a newer request is still live.
      if (isCurrent()) setGenerating(false);
    }
  }

  // "Stop waiting" = stop WATCHING. The HTTP wait is dropped, but the server operation
  // continues and its key is retained, so the reporter is told the truth and given a way
  // back to the result rather than a false "nothing was saved".
  function stopWatchingAnalysis() {
    generateAbortRef.current?.abort();
    generateAbortRef.current = null;
    generateAttemptRef.current += 1;
    setGenerating(false);
    setAnalysisPending(true);
    announce(
      'PRIME is still working on the analysis in the background. Nothing is lost — use “Check if it is ready” when you want to look.',
    );
  }

  function updateRca(field, value) {
    rcaDirtyRef.current = true;
    rcaEditSequenceRef.current += 1;
    setRcaDirty(true);
    markDirty();
    setRcaContent((current) => ({ ...(current || {}), [field]: value }));
    setAcknowledged(false);
  }

  async function approveAnalysis() {
    setActionError(null);
    setValidationKeys([]);
    setBusy(true);
    try {
      // Flush any pending edits first: confirming text that has not reached the
      // server would stamp an approval on the wrong version. A failed flush stops the
      // confirmation entirely.
      const saved = await requireSavedDraft('Confirmation');
      if (!saved) return;
      const response = await approveRequestAnalysis(saved.id, saved.revision);
      applyIntake(response?.data);
      announce('You confirmed the analysis. You can send the request now.');
    } catch (error) {
      if (error?.details?.reason === 'analysis_incomplete') {
        setValidationKeys(error.details.missing || []);
        setTimeout(() => validationSummaryRef.current?.focus(), 0);
      } else {
        setActionError(error?.message || 'PRIME could not record your confirmation.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendRequest() {
    setActionError(null);
    setValidationKeys([]);
    setBusy(true);
    try {
      const saved = await requireSavedDraft('Submission');
      if (!saved) return;
      const prepared = await prepareRequestIntake(saved.id, saved.revision);
      const readyIntake = applyIntake(prepared?.data);
      const submitted = await submitRequestIntake(readyIntake.id, readyIntake.revision, {
        idempotencyKey: submitKeyRef.current,
      });
      setSubmission(submitted?.data || null);
      applyIntake(submitted?.data?.intake || readyIntake);
      setStep('submitted');
    } catch (error) {
      if (error?.details?.reason === 'intake_not_ready') {
        setValidationKeys(error.details.missing || []);
        setTimeout(() => validationSummaryRef.current?.focus(), 0);
      } else {
        setActionError(error?.message || 'PRIME could not send this request.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <section className="support-ticket-page">
        <PageHeader
          eyebrow="Help"
          title="Request a feature or service"
          subtitle="Loading your draft…"
        />
        <p className="support-ticket-card-hint" role="status">
          Loading your saved draft…
        </p>
      </section>
    );
  }

  if (loadState === 'error') {
    return (
      <section className="support-ticket-page">
        <PageHeader eyebrow="Help" title="Request a feature or service" />
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="request-load-error"
        >
          <h3>PRIME could not open this draft</h3>
          <p>{loadError?.message || 'The draft could not be loaded.'}</p>
          <p>
            <Link to="/feedback/request/new">Start a new request</Link> or{' '}
            <Link to="/feedback">go to My Feedback</Link>.
          </p>
        </div>
      </section>
    );
  }

  const currentStepIndex = STEPS.findIndex((entry) => entry.id === step);
  const rca = intake?.preliminaryRca || null;
  // `rcaDirty` mirrors rcaDirtyRef into render state. The ref is what buildPayload
  // reads (it must be current synchronously); this is what the UI gates on.
  // Submission requires a confirmation that applies to the text ON SCREEN, so an
  // unsaved edit blocks it exactly as a missing confirmation does.
  const approved = Boolean(rca?.approvedAt) && !rcaDirty;
  const canGenerate = coverage.ready;

  return (
    <section className="support-ticket-page request-intake-page" data-testid="request-intake-page">
      <PageHeader
        eyebrow="Help"
        title="Request a feature or service"
        subtitle="Answer a few questions, check PRIME’s draft analysis, then send it to the product team."
      />

      <ol className="support-ticket-steps support-ticket-no-print" data-testid="request-steps">
        {STEPS.map((entry, index) => (
          <li
            key={entry.id}
            aria-current={entry.id === step ? 'step' : undefined}
            data-complete={index < currentStepIndex ? 'true' : 'false'}
          >
            <span className="step-index" aria-hidden="true">
              {index < currentStepIndex ? '✓' : index + 1}
            </span>
            <span>{entry.label}</span>
          </li>
        ))}
      </ol>

      {/* One polite live region for save state, new turns, and progress, so a
          screen-reader user hears what changed without being interrupted mid-type. */}
      <p
        className={`support-ticket-savestate support-ticket-no-print${saveState === 'failed' ? ' is-failed' : ''}`}
        aria-live="polite"
        data-testid="request-live-note"
      >
        {saveMessage || liveNote}
      </p>

      {actionError ? (
        <div className="support-ticket-alert tone-danger" role="alert" data-testid="request-error">
          <h3>Something went wrong</h3>
          <p>{actionError}</p>
        </div>
      ) : null}

      {validationKeys.length ? (
        <div
          className="support-ticket-alert tone-danger support-ticket-no-print"
          role="alert"
          tabIndex={-1}
          ref={validationSummaryRef}
          data-testid="request-validation-summary"
        >
          <h3>
            {validationKeys.length} thing{validationKeys.length === 1 ? '' : 's'} still need your
            attention
          </h3>
          <ul>
            {validationKeys.map((key) => (
              <li key={key}>{REQUEST_VALIDATION_LABELS[key] || key}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {step === 'converse' ? (
        <>
          <div className="support-ticket-card">
            <h2>What should PRIME call this request?</h2>
            <div className="form-field">
              <label className="form-label" htmlFor="request-title">
                Short summary
              </label>
              <div className="form-control">
                <input
                  className="form-input"
                  id="request-title"
                  name="title"
                  type="text"
                  value={title}
                  maxLength={TITLE_MAX_LENGTH}
                  aria-describedby="request-title-hint"
                  onChange={(event) => {
                    markDirty();
                    setTitle(event.target.value);
                  }}
                />
              </div>
              <div className="form-hint" id="request-title-hint">
                One line the product team will see in their queue, for example &ldquo;One-click
                financial roll-up export&rdquo;.
              </div>
            </div>
            <div className="form-field">
              <label className="form-label" htmlFor="request-description">
                In your own words (optional)
              </label>
              <div className="form-control">
                <textarea
                  className="form-input form-textarea"
                  id="request-description"
                  name="description"
                  rows={4}
                  value={description}
                  maxLength={MESSAGE_MAX_LENGTH}
                  aria-describedby="request-description-hint"
                  onChange={(event) => {
                    markDirty();
                    setDescription(event.target.value);
                  }}
                />
              </div>
              <div className="form-hint" id="request-description-hint">
                Start anywhere. The questions below will fill in the rest.
              </div>
            </div>
          </div>

          <ProgressPanel coverage={coverage} />

          <div className="support-ticket-card request-conversation">
            <h2>Your conversation with PRIME</h2>
            <ol className="request-transcript" data-testid="request-transcript">
              {messages.length === 0 ? (
                <li className="request-turn is-empty" data-testid="request-transcript-empty">
                  Nothing yet. Ask PRIME for the first question, or just start typing.
                </li>
              ) : null}
              {messages.map((message) => (
                <li
                  key={message.id}
                  className={`request-turn ${message.actorType === 'user' ? 'is-user' : 'is-assistant'}`}
                  data-testid={`request-turn-${message.actorType}`}
                  data-source={messageSource(message) || undefined}
                >
                  <p className="request-turn-meta">
                    <b>{message.actorType === 'user' ? 'You' : 'PRIME'}</b>
                    {message.actorType === 'assistant' ? (
                      <>
                        {' · '}
                        <span data-testid="request-turn-source">{turnSourceLabel(message)}</span>
                      </>
                    ) : null}
                    {messageTopic(message) ? (
                      <>
                        {' · '}
                        <span>{REQUEST_PROBE_TOPIC_LABELS[messageTopic(message)]}</span>
                      </>
                    ) : null}
                  </p>
                  <p className="request-turn-body">{message.content}</p>
                </li>
              ))}
              <li ref={transcriptEndRef} aria-hidden="true" className="request-transcript-end" />
            </ol>

            {lastAssistantTurn ? (
              <p className="support-ticket-card-hint" data-testid="request-current-question">
                PRIME asked: {lastAssistantTurn.content}
              </p>
            ) : null}

            <div className="form-field">
              <label className="form-label" htmlFor="request-answer">
                Your answer
              </label>
              <div className="form-control">
                <textarea
                  className="form-input form-textarea"
                  id="request-answer"
                  name="answer"
                  rows={3}
                  ref={messageInputRef}
                  value={draft}
                  maxLength={MESSAGE_MAX_LENGTH}
                  aria-describedby="request-answer-hint"
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleDraftKeyDown}
                />
              </div>
              <div className="form-hint" id="request-answer-hint">
                Press Enter to send. Shift and Enter together start a new line.
              </div>
            </div>

            <div className="support-ticket-actions support-ticket-no-print">
              <button
                type="button"
                className="btn primary"
                data-testid="request-send-answer"
                disabled={busy || !draft.trim()}
                onClick={() => void sendAnswer()}
              >
                {busy ? 'Sending…' : 'Send'}
              </button>
              <button
                type="button"
                className="btn ghost"
                data-testid="request-next-question"
                disabled={busy}
                onClick={() => void askNextQuestion()}
              >
                Ask PRIME for the next question
              </button>
              <button
                type="button"
                className="btn ghost"
                data-testid="request-save"
                disabled={busy || saveState === 'saving'}
                onClick={() => void save()}
              >
                Save draft
              </button>
            </div>
          </div>

          <div className="support-ticket-card support-ticket-no-print">
            <h2>Ready for PRIME to pull this together?</h2>
            <p className="support-ticket-card-hint" data-testid="request-generate-hint">
              {canGenerate
                ? 'PRIME will draft a preliminary analysis from your answers. You will be able to correct every part of it before anything is sent.'
                : `Answer at least ${REQUEST_MINIMUM_PROBED_TOPICS} questions, including the core problem and what you want to be able to do, and PRIME can draft an analysis. ${coverage.covered.length} answered so far.`}
            </p>
            {generating ? (
              <div className="support-ticket-actions">
                {/* Visible progress only — no `role="status"`. `generateAnalysis`
                    already announced "PRIME is drafting a preliminary analysis…"
                    through this page's single live region, and an implicit polite
                    region here said nearly the same words at the same moment
                    (typed-intake commit 7 visual/a11y audit). */}
                <p className="support-ticket-delivery-state" data-glyph="i">
                  PRIME is working on the analysis. This can take a few seconds.
                </p>
                <button
                  type="button"
                  className="btn ghost"
                  data-testid="request-cancel-generate"
                  onClick={stopWatchingAnalysis}
                >
                  Stop waiting
                </button>
              </div>
            ) : analysisPending ? (
              /* The operation is still running server side. There is deliberately NO
                 "generate" button here: a second press cannot start a second analysis
                 (one key, one operation), and offering it would imply otherwise. */
              <div className="support-ticket-actions" data-testid="request-analysis-pending">
                {/* Same reason as the generating branch above: `stopWatchingAnalysis`
                    and the IDEMPOTENCY_IN_PROGRESS path both announce this through the
                    page's one live region, so this paragraph is visible text only. */}
                <p className="support-ticket-delivery-state" data-glyph="i">
                  PRIME is still working on the analysis in the background.
                </p>
                <button
                  type="button"
                  className="btn primary"
                  data-testid="request-check-analysis"
                  disabled={busy}
                  onClick={() => void generateAnalysis(null, { resume: true })}
                >
                  Check if it is ready
                </button>
              </div>
            ) : (
              <div className="support-ticket-actions">
                <button
                  type="button"
                  className="btn primary"
                  data-testid="request-generate"
                  disabled={busy || !canGenerate}
                  onClick={() => void generateAnalysis('ai')}
                >
                  Generate preliminary analysis
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  data-testid="request-write-own"
                  disabled={busy}
                  onClick={() => void generateAnalysis('deterministic')}
                >
                  I would rather write it myself
                </button>
              </div>
            )}
            {analysisFailure ? (
              <div
                className="support-ticket-alert tone-warn"
                role="alert"
                data-testid="request-analysis-failure"
              >
                <h3>PRIME could not generate the analysis</h3>
                <p>{analysisFailure.message}</p>
                <p>
                  {analysisFailure.reason === 'sensitive_content_blocked'
                    ? 'PRIME did not send your conversation to the AI assistant, because it looks like it contains protected information. Nothing left PRIME. Remove it from the conversation, or write the problem statement yourself — that path sends nothing anywhere.'
                    : analysisFailure.reason === 'provider_fallback'
                      ? 'PRIME’s AI provider was unavailable, so nothing was drafted. Nothing has been sent, and your conversation is saved.'
                      : 'Nothing was saved, because PRIME will not store an analysis it cannot check. Your conversation is safe.'}
                </p>
                <div className="support-ticket-actions">
                  <button
                    type="button"
                    className="btn primary"
                    data-testid="request-analysis-failure-deterministic"
                    disabled={busy}
                    onClick={() => void generateAnalysis('deterministic')}
                  >
                    Write the problem statement myself
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    data-testid="request-analysis-failure-retry"
                    disabled={busy}
                    onClick={() => void generateAnalysis('ai')}
                  >
                    Try PRIME again
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="support-ticket-actions support-ticket-no-print">
            <Link className="btn ghost" to="/feedback">
              My Feedback
            </Link>
            {rca ? (
              <button
                type="button"
                className="btn ghost"
                data-testid="request-back-to-analysis"
                onClick={() => setStep('analysis')}
              >
                Back to the analysis
              </button>
            ) : null}
          </div>
        </>
      ) : null}

      {step === 'analysis' && rca ? (
        <>
          <RcaReviewSection
            rca={rca}
            content={rcaContent || rca.content}
            acknowledged={acknowledged}
            onAcknowledgeChange={setAcknowledged}
            onChange={updateRca}
            onApprove={() => void approveAnalysis()}
            onBack={() => setStep('converse')}
            onSave={() => void save()}
            busy={busy}
            saveState={saveState}
            // A local, unpersisted analysis edit un-confirms the analysis on screen
            // AND blocks submission, so the reporter can never send text they only
            // believe they confirmed.
            dirty={rcaDirty}
            validationKeys={validationKeys}
          />

          <div className="support-ticket-card support-ticket-no-print">
            <h3>Send this to the product team</h3>
            <p className="support-ticket-card-hint">
              PRIME files this in the governed feedback queue with the analysis you confirmed, your
              conversation, and a note of which system drafted it.
            </p>
            <div className="support-ticket-actions">
              <button
                type="button"
                className="btn primary"
                data-testid="request-submit"
                disabled={busy || !approved}
                onClick={() => void sendRequest()}
              >
                {busy ? 'Sending…' : 'Send the request'}
              </button>
              <button
                type="button"
                className="btn ghost"
                data-testid="request-regenerate"
                disabled={busy || approved || analysisPending}
                // The key is already fresh: the completed operation minted a new one when
                // it landed. Minting another here would abandon a key whose operation may
                // still be in flight.
                onClick={() => void generateAnalysis('ai')}
              >
                Ask PRIME to draft it again
              </button>
              {!approved ? (
                <span className="support-ticket-action-note" data-testid="request-submit-blocked">
                  Confirm the analysis above before sending.
                </span>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {step === 'submitted' ? (
        <div
          className="support-ticket-alert tone-success"
          role="status"
          data-testid="request-confirmation"
        >
          <h3>PRIME recorded your request</h3>
          <p>
            Your request is in the governed feedback queue with the analysis you confirmed. The
            product team can see it now.
          </p>
          {submission?.feedbackRecord?.id ? (
            <p className="support-ticket-card-hint">Reference {submission.feedbackRecord.id}.</p>
          ) : null}
          <p>
            <Link to="/feedback">Track it in My Feedback</Link>
            {intake?.id ? (
              <>
                {' · '}
                <Link to={`/feedback/request/${intake.id}`}>Open this request</Link>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      <p className="support-ticket-card-hint support-ticket-no-print">
        Signed in as {user?.displayName || 'a PRIME user'}. PRIME attaches this identity to the
        request for you.
      </p>
    </section>
  );
}

const REQUEST_VALIDATION_LABELS = Object.freeze({
  preliminaryRca: 'PRIME needs a preliminary analysis before this request can be sent.',
  'preliminaryRca.problemStatement': 'The problem statement is required.',
  'preliminaryRca.desiredOutcome': 'What you want to be able to do is required.',
  'preliminaryRca.approvedAt': 'Confirm the preliminary analysis before sending.',
  conversation: 'Answer at least one of PRIME’s questions.',
  title: 'A short summary is required.',
});

// Progress as an accessible LIST with text state, never a colour-coded bar: the
// meaning has to survive a monochrome display and a screen reader.
function ProgressPanel({ coverage }) {
  return (
    <section
      className="support-ticket-card request-progress"
      aria-labelledby="request-progress-heading"
      data-testid="request-progress"
    >
      <h2 id="request-progress-heading">What PRIME has, and what is still open</h2>
      {/* NOT a live region. This page has exactly ONE announcement channel
          (`request-live-note`), and it is deliberate: `announce()` clears the autosave
          line before writing progress so the two cannot mask each other. This
          paragraph used to carry its own `aria-live="polite"`, so every answer fired
          TWO polite announcements in the same tick — "Your answer was added." from the
          real channel and "4 of 15 topics answered." from here — and a screen-reader
          user heard the less important one interleaved with, or on top of, the one that
          told them their work had landed (typed-intake commit 7 visual/a11y audit).
          The count is still on screen and still in the labelled topic list below. */}
      <p className="support-ticket-card-hint">
        {coverage.covered.length} of {REQUEST_PROBE_TOPICS.length} topics answered.
        {coverage.ready
          ? ' That is enough for PRIME to draft an analysis.'
          : ` At least ${REQUEST_MINIMUM_PROBED_TOPICS} are needed, including the core problem and what you want to be able to do.`}
      </p>
      <ul className="request-progress-list">
        {REQUEST_PROBE_TOPICS.map((topic) => {
          const covered = coverage.covered.includes(topic);
          return (
            <li
              key={topic}
              data-covered={covered ? 'true' : 'false'}
              data-testid={`request-topic-${topic}`}
            >
              <span className="request-progress-glyph" aria-hidden="true">
                {covered ? '✓' : '·'}
              </span>
              <span>{REQUEST_PROBE_TOPIC_LABELS[topic]}</span>
              {/* The state in WORDS, so it is never conveyed by the glyph alone. */}
              <span className="request-progress-state">{covered ? 'Answered' : 'Open'}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default RequestIntakePage;
